import type {
  TemplateDetail,
  TemplateField,
  TemplateFieldType,
  TemplateSection,
} from '../../overview/models/template.types';
import { typeTakesOptions } from '../../overview/models/template.types';

/**
 * What a stored template means when somebody fills it in.
 *
 * The builder configures fields; this turns that configuration into a form. The
 * rules here are the backend's own (the template-form runtime rules, now served under `/purchase-requests`),
 * so the form and the server agree on which fields exist and what each accepts.
 *
 * Nothing here knows a template's name, id or kind. Every decision is made from
 * the field's own type, status and flags.
 */

/** What one answered field holds, as the form keeps it and the wire carries it. */
export type TemplateFormValue = string | number | boolean | string[] | null;

/** The form state: `{ [sectionKey]: { [fieldKey]: value } }`. */
export type TemplateFormValues = Record<string, Record<string, TemplateFormValue>>;

/** One entry of the submission payload. */
export interface TemplateFormFieldValue {
  sectionKey: string;
  fieldKey: string;
  value: TemplateFormValue;
}

/** The submission body, exactly as `POST /purchase-requests` reads it. */
export interface TemplateFormSubmissionPayload {
  templateId: number;
  /** The detail's `modifiedDate`, unchanged: the server's staleness check compares it. */
  templateModifiedDate: string;
  values: TemplateFormFieldValue[];
}

/** How a field type is drawn and what it holds. */
export type TemplateControlKind =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'url'
  | 'number'
  | 'currency'
  | 'date'
  | 'checkbox'
  | 'toggle'
  | 'choice'
  | 'multi'
  | 'upload'
  | 'file'
  | 'calculated';

const CONTROL_OF: Readonly<Record<TemplateFieldType, TemplateControlKind>> = {
  Text: 'text',
  'Short text': 'text',
  'Masked text': 'text',
  'Long text': 'textarea',
  'Address block': 'textarea',
  Email: 'email',
  Phone: 'phone',
  URL: 'url',
  Number: 'number',
  Currency: 'currency',
  Date: 'date',
  Checkbox: 'checkbox',
  /* An On / Off switch (design21, 2026-10-22): a boolean, Off an answer as much as On. */
  Toggle: 'toggle',
  /* Picked from the field's options; typed only while it has none (`fieldControlKind`). */
  Dropdown: 'choice',
  'Search and Select': 'choice',
  Radio: 'choice',
  'Multi-select': 'multi',
  'Multi-select Dropdown': 'multi',
  /* One file, answered as a data URI with its name. */
  'File upload': 'upload',
  /* Answered as one uploaded file, as a File upload is (2026-10-14) -- Supplier's Quality Certifications. */
  'Multi-select + file': 'upload',
  Calculated: 'calculated',
};

/**
 * The most digits a Number answer holds -- the "Up to 12 digits" the builder's type list promises. The sign and the
 * decimal point are not digits; a lone leading zero ("0.5") is not counted either. The server applies the same rule.
 *
 * Twelve also keeps every answer exact: a JavaScript number holds integers to about 16 digits, so a longer one
 * comes back altered (88888888888888888 reads as 88888888888888900).
 */
export const NUMBER_MAX_DIGITS = 12;

/** The message a Number answer over {@link NUMBER_MAX_DIGITS} is refused with. */
export function numberDigitsMessage(label: string): string {
  return `${label} must be at most ${String(NUMBER_MAX_DIGITS)} digits`;
}

/** How many digits a number as typed ("-1234.50") holds, by {@link NUMBER_MAX_DIGITS}'s count. */
export function digitCountOf(text: string): number {
  const digits = text.replace(/[^\d.]/g, '');
  return digits.replace(/^0(?=\.)/, '').replace(/\./g, '').length;
}

/** How many digits a number holds, by {@link NUMBER_MAX_DIGITS}'s count. */
export function numberDigitCount(value: number): number {
  /* At or past 10^21 the formatter writes an exponent, and every such number is far over the limit anyway. */
  if (Math.abs(value) >= 1e21) return 22;
  return digitCountOf(Math.abs(value).toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 }));
}

/** The control a type is drawn as; an unknown type falls back to plain text. */
export function controlKindOf(fieldType: string): TemplateControlKind {
  return (CONTROL_OF as Readonly<Record<string, TemplateControlKind>>)[fieldType] ?? 'text';
}

/**
 * The control ONE FIELD is drawn as: its type, refined by its configured options.
 *
 * A choice type with options is a real list -- a select for Dropdown and Search
 * and Select, a radio group for Radio, a checkbox group for the multi-selects.
 * The same type with no options configured stays the free-text control it was
 * before options existed, so an older template is still answerable.
 */
export type TemplateFieldControlKind =
  | Exclude<TemplateControlKind, 'choice' | 'multi'>
  | 'choice-text'
  | 'select'
  | 'radio'
  | 'multi-text'
  | 'checklist';

/** The field's options as the form offers them: trimmed, blanks dropped. */
export function optionsOf(field: Pick<TemplateField, 'fieldType' | 'options'>): string[] {
  if (!typeTakesOptions(field.fieldType)) return [];
  return (field.options ?? []).map((option) => option.trim()).filter((option) => option !== '');
}

export function fieldControlKind(field: Pick<TemplateField, 'fieldType' | 'options'>): TemplateFieldControlKind {
  const kind = controlKindOf(field.fieldType);
  const hasOptions = optionsOf(field).length > 0;
  if (kind === 'choice') {
    if (!hasOptions) return 'choice-text';
    return field.fieldType === 'Radio' ? 'radio' : 'select';
  }
  if (kind === 'multi') return hasOptions ? 'checklist' : 'multi-text';
  return kind;
}

/** Whether a type is one the platform knows. */
export function isKnownFieldType(fieldType: string): boolean {
  return Object.prototype.hasOwnProperty.call(CONTROL_OF, fieldType);
}

/** Types that span the whole row of the grid. */
export function isFullWidth(fieldType: string): boolean {
  const kind = controlKindOf(fieldType);
  return kind === 'textarea' || kind === 'multi' || kind === 'file';
}
/**
 * Whether a value can be submitted for this type.
 *
 * File types wait on a file store (gap G2) and a calculated field has no formula
 * stored. Neither is in the schema nor the payload, and neither is required.
 */
export function isSubmittableType(fieldType: string): boolean {
  const kind = controlKindOf(fieldType);
  return kind !== 'file' && kind !== 'calculated';
}

/** A retired field keeps its row but is no longer part of the form. */
export function isRenderableField(field: TemplateField): boolean {
  return (field.statusName ?? '').trim().toLowerCase() !== 'inactive';
}

/** A field that is rendered AND takes a value. */
export function isSubmittableField(field: TemplateField): boolean {
  return isRenderableField(field) && isSubmittableType(field.fieldType);
}

/**
 * Helper text as a person should read it.
 *
 * System fields store their placeholder-like hint with its literal quotes --
 * `"Enter title"` -- which reads as a typo under a control.
 */
export function cleanHelperText(text: string | null | undefined): string {
  const trimmed = (text ?? '').trim();
  const unquoted = trimmed.replace(/^["“](.*)["”]$/s, '$1').trim();
  return unquoted;
}

/** The key a field's value and its errors live under, on both sides of the wire. */
export function formKey(sectionKey: string, fieldKey: string): string {
  return `${sectionKey}.${fieldKey}`;
}

/** One run of fields under a group heading, or under none. */
export interface RenderableFieldGroup {
  /** `null` for fields that belong to no group. */
  heading: string | null;
  fields: TemplateField[];
}

/** A section as the form draws it. */
export interface RenderableSection {
  sectionKey: string;
  title: string;
  groups: RenderableFieldGroup[];
  /** Every rendered field, in order -- the groups flattened. */
  fields: TemplateField[];
}

function byOrdinal<T extends { ordinal?: number }>(items: readonly T[]): T[] {
  /* Array order is the fallback, and a stable sort keeps it for equal ordinals. */
  return items
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        (a.item.ordinal ?? a.index) - (b.item.ordinal ?? b.index) || a.index - b.index,
    )
    .map(({ item }) => item);
}

function groupsOf(fields: readonly TemplateField[]): RenderableFieldGroup[] {
  const groups: RenderableFieldGroup[] = [];
  const byHeading = new Map<string, RenderableFieldGroup>();

  for (const field of fields) {
    const heading = field.fieldGroup?.trim() ? field.fieldGroup.trim() : null;
    if (heading === null) {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.heading === null) {
        lastGroup.fields.push(field);
      } else {
        groups.push({ heading: null, fields: [field] });
      }
      continue;
    }

    const existing = byHeading.get(heading);
    if (existing) {
      existing.fields.push(field);
    } else {
      const created = { heading, fields: [field] };
      byHeading.set(heading, created);
      groups.push(created);
    }
  }

  return groups;
}

/**
 * The PR Template's system fields the platform fills in rather than the Initiator (design16, 2026-10-19): the PR
 * Number and the Business Unit, Division and Department of the request's header, and each line's Purchase Group and
 * Cost Center Code. The server never asks for them either (`TemplateFormSubmissionService.isAutoFilledField`).
 */
const AUTO_FILLED_FIELDS: Readonly<Record<string, readonly string[]>> = {
  'pr-details': ['pr-number', 'business-unit', 'division', 'department'],
  'line-items': ['purchase-group', 'cost-center-code'],
};

/**
 * Whether a field is the record's own Status (design21, 2026-10-22): the system Toggle keyed `status` closing the PR
 * Template's Purchase Request Details and Supplier Onboarding's Legal & Company Details. It is not an answer -- a form
 * draws it as the record's Status switch and sends it as `statusName` -- so no form draws it as a field or sends it
 * (`TemplateFormSubmissionService.isRecordStatusField`).
 */
export function isRecordStatusField(field: Pick<TemplateField, 'fieldKey' | 'fieldType' | 'system'>): boolean {
  return field.system && field.fieldType === 'Toggle' && field.fieldKey === 'status';
}

/** Whether a field is one the platform fills in (see AUTO_FILLED_FIELDS), and so no form draws or sends it. */
export function isAutoFilledField(sectionKey: string, fieldKey: string): boolean {
  return AUTO_FILLED_FIELDS[sectionKey]?.includes(fieldKey) === true;
}

/**
 * The sections a form draws, in order, each rendered once.
 *
 * Sections and fields follow their ordinals; retired fields are dropped; a
 * section left with nothing to draw -- every kind's Template Information step --
 * is dropped with them.
 */
export function renderableSections(detail: Pick<TemplateDetail, 'sections'>): RenderableSection[] {
  return byOrdinal<TemplateSection>(detail.sections ?? [])
    .map((section) => {
      const fields = byOrdinal(section.fields ?? []).filter(
        (field) =>
          isRenderableField(field) &&
          !isAutoFilledField(section.sectionKey, field.fieldKey) &&
          !isRecordStatusField(field),
      );
      return {
        sectionKey: section.sectionKey,
        title: section.title,
        groups: groupsOf(fields),
        fields,
      };
    })
    .filter((section) => section.fields.length > 0);
}

/** The value an unanswered field starts from. */
export function emptyValueFor(fieldType: string): TemplateFormValue {
  switch (controlKindOf(fieldType)) {
    case 'number':
    case 'currency':
      return null;
    case 'checkbox':
    case 'toggle':
      return false;
    case 'multi':
      return [];
    default:
      return '';
  }
}

function isEmptyValue(value: TemplateFormValue | undefined): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function normalised(value: TemplateFormValue): TemplateFormValue {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map((entry) => entry.trim()).filter((entry) => entry !== '');
  return value;
}

/**
 * The submission body, built from the template -- never from the form state's keys.
 *
 * Walking the rendered submittable fields means a key left behind in the form
 * state (a field removed by a reload) cannot leak into the request. An optional
 * field left empty is omitted; an unticked optional checkbox is omitted too,
 * since `false` there only means "not answered".
 */
export function toSubmissionPayload(
  detail: Pick<TemplateDetail, 'id' | 'modifiedDate' | 'sections'>,
  values: TemplateFormValues,
): TemplateFormSubmissionPayload {
  const entries: TemplateFormFieldValue[] = [];

  for (const section of renderableSections(detail)) {
    for (const field of section.fields) {
      if (!isSubmittableType(field.fieldType)) continue;

      const raw = values[section.sectionKey]?.[field.fieldKey];
      const value = normalised(raw ?? emptyValueFor(field.fieldType));
      const unanswered =
        isEmptyValue(value) || (controlKindOf(field.fieldType) === 'checkbox' && value === false);
      if (unanswered && !field.mandatory) continue;

      entries.push({ sectionKey: section.sectionKey, fieldKey: field.fieldKey, value });
    }
  }

  return {
    templateId: detail.id,
    templateModifiedDate: detail.modifiedDate ?? '',
    values: entries,
  };
}
